import { useCallback, useRef, useState } from 'react';

/**
 * Workflow state for one client: review -> analysis -> health. The server owns the review; this hook
 * only keeps the latest server response and the version it belongs to. Any edit clears later stages.
 */
export function usePortfolioHealthCheck(api, clientId, pan = null) {
  const [review, setReview] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(null); // 'review' | 'edit' | 'analyse' | 'health' | null
  const [error, setError] = useState(null); // { stage, message, code }
  const seq = useRef(0);

  const run = useCallback(async (stage, fn) => {
    const my = ++seq.current;
    setBusy(stage); setError(null);
    try { return await fn(); } catch (e) {
      if (my === seq.current) setError({ stage, message: e.message, code: e.code });
      if (e.code === 'review_stale' || e.code === 'review_expired') { setAnalysis(null); setHealth(null); }
      return null;
    } finally { if (my === seq.current) setBusy(null); }
  }, []);

  const loadReview = useCallback((refresh = false) => run('review', async () => {
    const r = await api.holdingsSummary(clientId, refresh, pan);
    setReview(r); setAnalysis(null); setHealth(null);
    return r;
  }), [api, clientId, pan, run]);

  const edit = useCallback((edits) => run('edit', async () => {
    const r = await api.updateReview(clientId, review.reviewVersion, edits);
    setReview(r); setAnalysis(null); setHealth(null);
    return r;
  }), [api, clientId, review, run]);

  const search = useCallback((q) => api.searchSchemes(clientId, review.reviewVersion, q), [api, clientId, review]);

  const analyse = useCallback(() => run('analyse', async () => {
    const a = await api.analyse(clientId, review.reviewVersion);
    setAnalysis(a); setHealth(null);
    return a;
  }), [api, clientId, review, run]);

  const runHealth = useCallback(() => run('health', async () => {
    const h = await api.healthCheck(clientId, analysis.reviewVersion, analysis.analysisId);
    setHealth(h);
    return h;
  }), [api, clientId, analysis, run]);

  return { review, analysis, health, busy, error, loadReview, edit, search, analyse, runHealth };
}
